import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useCourses } from "../hooks/useCourses";
import { downloadCertificate } from "../utils/certificateDownload";

// Determina a URL base da API
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return "/api";
  }

  const envUrl = window.REACT_APP_BACKEND_URL;
  if (!envUrl || envUrl === "") {
    return "/api";
  }

  if (envUrl.endsWith("/api")) {
    return envUrl;
  }

  return envUrl + "/api";
};

const API_BASE_URL = getApiBaseUrl();

const CoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { courses } = useCourses(user?.id);

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [courseProgress, setCourseProgress] = useState({});
  const [completingVideo, setCompletingVideo] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [loadingCourse, setLoadingCourse] = useState(true);
  const [courseNotFound, setCourseNotFound] = useState(false);

  // Get the back navigation URL based on fromGroup parameter
  const getBackUrl = () => {
    const fromGroup = searchParams.get("fromGroup");
    return fromGroup ? `/dashboard/group/${fromGroup}` : "/dashboard";
  };

  // Find course by ID
  useEffect(() => {
    if (courses && courseId) {
      setLoadingCourse(true);
      setCourseNotFound(false);
      
      const course = courses.find((c) => c.id === courseId);
      if (course) {
        setSelectedCourse(course);
        setLoadingCourse(false);
        loadCourseProgress(course.id);
      } else {
        // If course not found in current courses, try to fetch it directly
        fetchCourseDirectly(courseId);
      }
    }
  }, [courses, courseId]);

  // Fetch course directly from API
  const fetchCourseDirectly = async (courseId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/courses/${courseId}`);
      if (response.ok) {
        const course = await response.json();
        setSelectedCourse(course);
        setLoadingCourse(false);
        loadCourseProgress(course.id);
      } else {
        console.error("Course not found:", courseId);
        setCourseNotFound(true);
        setLoadingCourse(false);
      }
    } catch (error) {
      console.error("Error fetching course directly:", error);
      setCourseNotFound(true);
      setLoadingCourse(false);
    }
  };

  // Load course progress
  const loadCourseProgress = async (courseId) => {
    if (!user) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/courses/${courseId}/progress?user_id=${user.id}`
      );
      if (response.ok) {
        const progress = await response.json();
        setCourseProgress((prev) => ({
          ...prev,
          [courseId]: progress,
        }));
      }
    } catch (error) {
      console.error("Error loading course progress:", error);
    }
  };

  // Track video access
  const trackVideoAccess = async (videoIndex) => {
    try {
      const video = selectedCourse.videos[videoIndex];
      await fetch(`${API_BASE_URL}/track-video-access`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user.id,
          course_id: selectedCourse.id,
          video_id: video.id,
        }),
      });
    } catch (error) {
      console.error("Error tracking video access:", error);
    }
  };

  // Mark video as completed
  const markVideoAsCompleted = async (videoIndex) => {
    if (completingVideo) return;

    try {
      setCompletingVideo(true);
      const video = selectedCourse.videos[videoIndex];

      await trackVideoAccess(videoIndex);

      const progress = courseProgress[selectedCourse.id] || {};
      const completedVideos = progress.completed_videos || [];
      const newCompletedVideos = [...completedVideos];

      if (!newCompletedVideos.includes(video.id)) {
        newCompletedVideos.push(video.id);

        await fetch(`${API_BASE_URL}/courses/${selectedCourse.id}/progress`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: user.id,
            completed_videos: newCompletedVideos,
          }),
        });

        setCourseProgress((prev) => ({
          ...prev,
          [selectedCourse.id]: {
            ...prev[selectedCourse.id],
            completed_videos: newCompletedVideos,
          },
        }));
      }
    } catch (error) {
      console.error("Error marking video as completed:", error);
    } finally {
      setCompletingVideo(false);
    }
  };

  // Extract Vimeo video ID
  const extractVimeoId = (url) => {
    if (!url) return null;
    const match = url.match(/vimeo\.com\/(?:.*\/)?(\d+)/);
    return match ? match[1] : null;
  };

  // Navigation functions
  const goToNextVideo = () => {
    if (currentVideoIndex < selectedCourse.videos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
    }
  };

  const goToPreviousVideo = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(currentVideoIndex - 1);
    }
  };

  // Submit quiz
  const submitQuiz = async () => {
    if (quizSubmitting) return;

    try {
      setQuizSubmitting(true);

      const response = await fetch(`${API_BASE_URL}/quiz/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          course_id: selectedCourse.id,
          user_id: user.id,
          answers: quizAnswers,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setQuizResult(result);
        setShowQuiz(false);

        // Navigate to certificate success page if passed
        if (result.passed) {
          const fromGroup = searchParams.get("fromGroup");
          const certificateUrl = fromGroup
            ? `/certificates/courses/${selectedCourse.id}/details/${result.certificate_id}?fromGroup=${fromGroup}`
            : `/certificates/courses/${selectedCourse.id}/details/${result.certificate_id}`;
          navigate(certificateUrl);
        } else {
          // Show failure message
          alert(
            `Você obteve ${Math.round(
              result.score
            )}% de acerto. É necessário 80% para aprovação. Revise o conteúdo e tente novamente.`
          );
        }
      } else {
        const error = await response.json();
        alert(`Erro ao submeter quiz: ${error.detail}`);
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
      alert("Erro ao submeter quiz");
    } finally {
      setQuizSubmitting(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loadingCourse) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando curso...</p>
        </div>
      </div>
    );
  }

  if (courseNotFound || !selectedCourse) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Curso não encontrado</h2>
          <p className="text-gray-600 mb-4">Por favor, selecione um curso no dashboard.</p>
          <button
            onClick={() => navigate(getBackUrl())}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  const progress = courseProgress[selectedCourse.id] || {};
  const completedVideos = progress.completed_videos || [];
  const currentVideo = selectedCourse.videos?.[currentVideoIndex];

  // Check if all videos are completed
  const allVideosCompleted =
    selectedCourse.videos?.length > 0 && completedVideos.length === selectedCourse.videos.length;

  // Check if quiz exists
  const hasQuiz = selectedCourse.quiz_questions?.length > 0;

  // Check if already has certificate
  const hasCertificate = progress.certificate_id;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(getBackUrl())}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{selectedCourse.title}</h1>
                <p className="text-gray-600 text-sm">
                  Vídeo {currentVideoIndex + 1} de {selectedCourse.videos?.length || 0} -{" "}
                  {currentVideo?.title}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                <button
                  onClick={handleLogout}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quiz Modal */}
      {showQuiz && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Quiz Final</h2>
                <button
                  onClick={() => setShowQuiz(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {selectedCourse.quiz_questions?.map((question, index) => (
                <div key={index} className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {index + 1}. {question.question}
                  </h3>
                  <div className="space-y-2">
                    {question.options?.map((option, optionIndex) => (
                      <label
                        key={optionIndex}
                        className="flex items-center space-x-3 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name={`question-${index}`}
                          value={optionIndex}
                          checked={quizAnswers[index] === optionIndex}
                          onChange={(e) => {
                            const newAnswers = [...quizAnswers];
                            newAnswers[index] = parseInt(e.target.value);
                            setQuizAnswers(newAnswers);
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                        />
                        <span className="text-gray-700">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              <div className="flex justify-between pt-6 border-t">
                <button
                  onClick={() => setShowQuiz(false)}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancelar
                </button>
                <button
                  onClick={submitQuiz}
                  disabled={quizSubmitting || quizAnswers.some((answer) => answer === null)}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {quizSubmitting ? "Enviando..." : "Finalizar Quiz"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Course Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Video Player */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {currentVideo && extractVimeoId(currentVideo.vimeo_url) ? (
                <div className="aspect-video">
                  <iframe
                    src={`https://player.vimeo.com/video/${extractVimeoId(
                      currentVideo.vimeo_url
                    )}?h=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                    title={currentVideo.title}
                  ></iframe>
                </div>
              ) : (
                <div className="aspect-video bg-gray-100 flex items-center justify-center">
                  <div className="text-center">
                    <svg
                      className="w-16 h-16 text-gray-400 mx-auto mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-gray-500">Vídeo não disponível</p>
                  </div>
                </div>
              )}

              {/* Video Controls */}
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {currentVideo?.title || "Vídeo não encontrado"}
                  </h2>
                  <div className="flex items-center space-x-2">
                    {!completedVideos.includes(currentVideo?.id) && (
                      <button
                        onClick={() => markVideoAsCompleted(currentVideoIndex)}
                        disabled={completingVideo}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                      >
                        {completingVideo ? "Marcando..." : "Marcar como Concluído"}
                      </button>
                    )}
                    {completedVideos.includes(currentVideo?.id) && (
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        ✓ Concluído
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-gray-600 mb-6">{currentVideo?.description}</p>

                {/* Navigation Controls */}
                <div className="flex justify-between">
                  <button
                    onClick={goToPreviousVideo}
                    disabled={currentVideoIndex === 0}
                    className="flex items-center bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    Anterior
                  </button>

                  <button
                    onClick={goToNextVideo}
                    disabled={currentVideoIndex >= selectedCourse.videos.length - 1}
                    className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próximo
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Video List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Lista de Vídeos</h3>
              <div className="space-y-2">
                {selectedCourse.videos?.map((video, index) => {
                  const isCompleted = completedVideos.includes(video.id);
                  const isCurrent = index === currentVideoIndex;
                  return (
                    <div
                      key={video.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        isCurrent
                          ? "bg-blue-50 border-blue-200 text-blue-800"
                          : isCompleted
                          ? "bg-green-50 border-green-200 text-green-800"
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                      }`}
                      onClick={() => setCurrentVideoIndex(index)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                              isCurrent
                                ? "bg-blue-500 text-white"
                                : isCompleted
                                ? "bg-green-500 text-white"
                                : "bg-gray-300 text-gray-600"
                            }`}
                          >
                            {isCurrent ? "▶" : isCompleted ? "✓" : index + 1}
                          </div>
                          <span className="font-medium text-sm">{video.title}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Progress */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Seu Progresso</h3>
              <div className="mb-2">
                <div className="flex justify-between text-sm mb-1">
                  <span>Progresso</span>
                  <span>
                    {Math.round(
                      (completedVideos.length / (selectedCourse.videos?.length || 1)) * 100
                    )}
                    %
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{
                      width: `${
                        (completedVideos.length / (selectedCourse.videos?.length || 1)) * 100
                      }%`,
                    }}
                  ></div>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {completedVideos.length} de {selectedCourse.videos?.length || 0} vídeos concluídos
              </p>
            </div>

            {/* Quiz Section */}
            {allVideosCompleted && hasQuiz && !hasCertificate && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quiz Final</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Parabéns! Você concluiu todos os vídeos. Agora é hora de testar seus
                  conhecimentos.
                </p>
                <button
                  onClick={() => {
                    setQuizAnswers(
                      new Array(selectedCourse.quiz_questions?.length || 0).fill(null)
                    );
                    setShowQuiz(true);
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Iniciar Quiz
                </button>
              </div>
            )}

            {/* Certificate */}
            {hasCertificate && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Certificado</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Parabéns! Você concluiu o curso e obteve seu certificado.
                </p>
                <button
                  onClick={() => downloadCertificate(progress.certificate_id)}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium text-center block"
                >
                  Baixar Certificado
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePage;
